import { useDeepMemo } from '@/renderer/utils/hooks/useDeepMemo';
import { deepEqual } from 'fast-equals';
import { useMemo, useRef } from 'react';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
// import { create, serverOnly, withPageContext } from 'vike-react-zustand';

import { IS_SERVER } from '@/renderer/utils/ssr';
import useClientInteractive from '@/renderer/utils/useInteractive';
import { useRequestFetcher } from '@/renderer/utils/useRequest';

import { useAsync } from 'react-streaming';
import { useEffectOnce } from '../hooks/useEffectOnce';
import { useIsFirstRender } from '../hooks/useIsFirstRender';
import { useReconcileOnForeground } from '../hooks/useReconcileOnForeground';
import { useReconcileOnReconnect } from '../hooks/useReconcileOnReconnect';
import { useRefUpdating } from '../hooks/useRefUpdating';
import { useUpdateEffect } from '../hooks/useUpdateEffect';
import { useInfiniteStore } from './InfiniteStoreProvider';
import type {
  InfiniteStore,
  UseInfiniteRequestOpts,
  UseInfiniteRequestResult,
  UseRequestOpts,
} from './types';
import { useNextInfiniteActions } from './useInfiniteActions';
import { useNextInfinitePolling } from './useInfinitePolling';
import { useNextInfiniteWebSocket } from './useInfiniteWebSocket';

//

const ID = 'id';
const DEFAULT_BY = ID;
const DEFAULT_DIR = 'DESC';
const LIMIT = 50;

//

const _noop = () => {
  /* void */
};

const baseOpts = {};

function useNextInfiniteInitialOpts<
  const ItemType extends API.DbItem,
  const BodyType = void,
  const QueryType extends API.BasePageInput = API.WithPageInput<ItemType>,
  const MetaType = void,
  const FromSocketType = ItemType,
>(
  key: string,
  givenOpts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
) {
  const opts = givenOpts || baseOpts;
  const modifiedBaseQueryRef = useRef<QueryType>();
  const modifiedBaseQuery = useMemo(() => {
    const by = opts?.query?.order?.by;
    const dir = opts?.query?.order?.dir;

    const nextOne =
      by && dir
        ? opts?.query
        : ({
            ...opts.query,
            order: {
              by: by || DEFAULT_BY,
              dir: dir || DEFAULT_DIR,
            },
          } as QueryType);

    // As a backup, in case something goes totally bonkers, let's deep equals here

    if (deepEqual(nextOne, modifiedBaseQueryRef.current)) {
      return modifiedBaseQueryRef.current;
    }

    modifiedBaseQueryRef.current = nextOne;
    return nextOne;
  }, [opts]);

  const initialOptsRef = useRef<UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>>();
  const initialOpts = useMemo(() => {
    const initialQuery = opts?.initialQuery;

    const orderParam = initialQuery?.order || modifiedBaseQuery?.order;
    const by = orderParam?.by || DEFAULT_BY;
    const dir = orderParam?.dir || DEFAULT_DIR;
    const pageParam = (initialQuery || modifiedBaseQuery)?.page;
    const initPageDirection = dir === 'DESC' ? ('after' as const) : ('before' as const);
    const initCursor = pageParam?.[initPageDirection];

    const next = {
      // ...opts,
      // We force the useAsync key to be used
      id: key,
      storeRaw: true,
      clientOnly: !!opts?.serverSkip,
      // If paused, it's manual, effectively short-circuiting the fetch
      // otherwise, on server, we tell it we want to fetch so that it at least sets pending:true, even if serverSkip
      // but if we are on the client, it is always TRUE, because we have to run it manually to hydrate the cache on load
      manual: !!opts?.pause && !IS_SERVER,
      query: {
        ...(initialQuery || modifiedBaseQuery),
        order: {
          ...orderParam,
          by,
          dir,
        },
        page: {
          ...(initialQuery?.page || modifiedBaseQuery?.page),
          limit: pageParam?.limit || LIMIT,
          ...(initCursor ? { [initPageDirection]: initCursor } : undefined),
        },
      } as QueryType,
    } as UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>;

    // Same deep-equals backstop as `modifiedBaseQuery` above, and for a sharper
    // reason (#2059). This memo depends on the whole `opts` OBJECT, but it only
    // ever reads `query` / `initialQuery` / `serverSkip` / `pause`. Any unrelated
    // field changing identity therefore minted a fresh `initialOpts`, and the
    // "refetch if the base params were changed" effect downstream treats that as
    // a base-param change and re-reads the list FROM SCRATCH.
    //
    // At release time that fired on every connected client at once: a release
    // message arrives over the WebSocket, MessageReceiptList recomputes its live-
    // window cadence, `setPollingDelays` hands `useNextInfiniteRequest` a new opts
    // object, and ~250ms later every release center in the fleet issued the same
    // uncached `order[by]=id&order[dir]=DESC&page[limit]=50` feed query inside the
    // same moment — 110 of the 118 release-time 503s. The list is already live
    // over the socket, so the refetch bought nothing; the cadence change reaches
    // the poller through its own ref either way.
    //
    // Guarding on the PRODUCED object (plain data only — no functions) means the
    // effect now fires when the request actually changed, which is what it says.
    const previous = initialOptsRef.current;
    if (previous && deepEqual(next, previous)) {
      return previous;
    }

    initialOptsRef.current = next;
    return next;
  }, [key, opts, modifiedBaseQuery]);

  return useMemo(() => initialOpts, [initialOpts]);
}

function useNextInfiniteBaseRequest<
  const ItemType extends API.DbItem,
  const BodyType = void,
  const QueryType extends API.BasePageInput = API.WithPageInput<ItemType>,
  const MetaType = void,
  const FromSocketType = ItemType,
>(
  key: string,
  opts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
  initialOpts: UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>,
) {
  const requestFetcher = useRequestFetcher(opts?.req, initialOpts);

  const isFirst = useIsFirstRender();
  const shouldRun = !opts?.pause && (IS_SERVER ? !opts?.serverSkip : opts?.serverSkip || isFirst);

  const abortController = useRef<AbortController>();

  const initialResult = useAsync([key, '-init'], async () => {
    if (shouldRun) {
      const nextAbortController = new AbortController();

      if (nextAbortController !== abortController.current) {
        if (abortController.current && !abortController.current?.signal.aborted) {
          // abort previous initial result request
          abortController.current.abort?.();
        }

        abortController.current = nextAbortController;
      }

      return await requestFetcher.run(undefined, {
        dontStoreError: true,
        dontAlterState: true,
        rawResult: true,
        throwOnRun: false,
        returnError: true,
        req: {
          init: {
            // signal
          },
        },
      });
    }
  });

  const initialIsNull = initialResult === null;
  const initialIsError = !initialIsNull && initialResult instanceof Error;
  const initialHasArray = !initialIsNull && !initialIsError && Array.isArray(initialResult?.data);

  const error = initialIsError ? initialResult : null;
  const data = initialIsNull || initialIsError ? null : initialResult;

  const length = initialHasArray ? initialResult?.data?.length || 0 : 0;

  const isPending =
    !opts?.pause &&
    (IS_SERVER ? !!opts?.serverSkip : !initialHasArray && !initialIsNull && !initialIsError);

  const isEmpty = !isPending && length === 0;

  const payload = useMemo(() => {
    return {
      data,
      error,
      isEmpty,
      isError: initialIsError,
      isFetching: false,
      isFetchingNextPage: false,
      isFetchingPreviousPage: false,
      isLoading: false,
      // Mark pending if in SSR & we don't run it until client
      isPending,
      isPlaceholderData: false,
      isRefetching: false,
      // Success only when result data is an array, even if it has no items, or request is completely skipped
      isSuccess: initialHasArray || isEmpty,
    };
  }, [data, error, isEmpty, initialIsError, isPending, initialHasArray, length]);

  const payloadMemoized = useDeepMemo(payload);

  return useMemo(
    () => [payloadMemoized, requestFetcher] as const,
    [payloadMemoized, requestFetcher],
  );
}

export function useNextInfiniteRequest<
  const ItemType extends API.DbItem,
  const BodyType = void,
  const QueryType extends API.BasePageInput = API.WithPageInput<ItemType>,
  const MetaType = void,
  const FromSocketType = ItemType,
>(
  givenOpts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
): UseInfiniteRequestResult<ItemType, MetaType> {
  const opts = givenOpts || baseOpts;

  // Every hook use should have its own "sub-ID" for the storage key; maybe we should hash the opts
  const id = 'infinity'; // @todo this actually breaks, and I don't yet know why: useId();

  // ...unless the key contains :, in which case we assume it has its own sub-ID
  const key = useMemo(
    () => `∞${opts?.key.includes(':') ? opts?.key : (`${opts?.key}:${id}` as const)}` as const,
    [opts?.key, id],
  );

  const deepMemoOpts = useDeepMemo(opts);

  const initialOpts = useNextInfiniteInitialOpts(key, deepMemoOpts);

  // On the server, this will return the initial data set
  const [basePayload, requestFetcher] = useNextInfiniteBaseRequest(key, deepMemoOpts, initialOpts);

  // On the client, we will switch to using the store after initial render
  const storeSet = useInfiniteStore((state: InfiniteStore<ItemType, MetaType>) => state.set);

  const storeGet = useInfiniteStore((state: InfiniteStore<ItemType, MetaType>) => state.get);
  const storeRemove = useInfiniteStore((state: InfiniteStore<ItemType, MetaType>) => state.remove);
  const storeBank = useInfiniteStore(
    (state: InfiniteStore<ItemType, MetaType>) => state.banks[key],
  );

  // ...but we need to use basePayload initially, as store is not yet populated
  // (this will be easier when vike-react-zustand is implemented, and is hydrated from the server directly)
  const bank = storeBank?.data === undefined ? basePayload : storeBank;

  // Keep track of the IDs we know about across different methods
  const _knownIdsRef2 = useRef<Set<API.Id>>(
    new Set(bank?.data?.data?.map?.((item) => item.id) || []),
  );
  const knownIdsRef = useRef<Set<API.Id>>(
    new Set(bank?.data?.data?.map?.((item) => item.id) || []),
  );

  // WS + Polling wait for client to be fully mounted before doing shenanigans like mangling state
  const isClientReady =
    useClientInteractive() &&
    (!!bank?.data || !!bank?.error || !bank?.isPending) &&
    !deepMemoOpts?.pause;

  // Intentionally left `false`: this keeps the fallback poll in its fast cadence
  // at all times (the "degraded" branch), which is exactly what #1210 wants —
  // WS is treated as best-effort and the poll is the reliable safety net. We do
  // NOT sync it to `ws.ready`, because backing the poll off to WS_CONNECTED_MAX
  // while "connected" is what let a silently-dropped release stay invisible.
  // Timely reconnect reconciliation is handled by useReconcileOnReconnect below.
  const wsReadyRef = useRef(false);

  // Poll for new messages, independently of websockets, in case WS fails, is unsupported, or misses messages
  const pollNow = useNextInfinitePolling(
    isClientReady,
    key,
    deepMemoOpts,
    initialOpts,
    knownIdsRef,
    requestFetcher,
    basePayload,
    storeBank,
    storeGet,
    storeSet,
    storeRemove,
    wsReadyRef,
  );

  const ws = useNextInfiniteWebSocket(
    isClientReady,
    key,
    deepMemoOpts,
    initialOpts,
    knownIdsRef,
    requestFetcher,
    basePayload,
    storeBank,
    storeGet,
    storeSet,
    storeRemove,
    wsReadyRef,
  );

  // When the socket comes back after dropping (or flapping while it reported
  // "connected"), immediately catch up from the newest cursor. This is the fix
  // for a newly-created release not rendering live until a full remount (#1210).
  useReconcileOnReconnect(ws.ready, pollNow);

  // Catch up on wake/foreground too, not only on reconnect. A backgrounded
  // mobile client can miss a message while the OS throttles the socket and then
  // resume with the socket judged healthy — so reconcile-on-reconnect never
  // fires. Page Visibility + native app-state run the same idempotent catch-up
  // the moment the app returns to the foreground, so a missed live message
  // surfaces immediately instead of waiting for the next WS event (#1659).
  useReconcileOnForeground(pollNow);

  // Payload actions

  const [fetchingStates, fetchNextPage, fetchPreviousPage, refetch, renderPayload, reset] =
    useNextInfiniteActions(
      key,
      deepMemoOpts,
      initialOpts,
      knownIdsRef,
      requestFetcher,
      basePayload,
      storeBank,
      storeGet,
      storeSet,
      storeRemove,
      wsReadyRef,
    );

  const _keyRef = useRefUpdating(key);

  const length = bank?.data?.data?.length || 0;
  const newestKey = initialOpts?.query?.order?.dir === 'DESC' ? 0 : (length || 1) - 1;
  const oldestKey = initialOpts?.query?.order?.dir === 'DESC' ? (length || 1) - 1 : 0;
  const newestItem = bank?.data?.data?.[newestKey];
  const oldestItem = bank?.data?.data?.[oldestKey];
  const hasNextPage = !!bank?.data?.pageInfo?.hasNextPage;
  const hasPreviousPage = !!bank?.data?.pageInfo?.hasPreviousPage;
  // return {
  //   data: {
  //     data: [],
  //     pageInfo: {
  //       limit: LIMIT,
  //       total: 0,
  //       order: {
  //         by: DEFAULT_BY,
  //         dir: DEFAULT_DIR,
  //       },
  //     },
  //   },
  //   error: undefined,
  //   newestItem: undefined,
  //   oldestItem: undefined,
  //   hasNextPage: false,
  //   hasPreviousPage: false,
  //   isEmpty: true,
  //   isError: false,
  //   isFetching: false,
  //   isFetchingNextPage: false,
  //   isFetchingPreviousPage: false,
  //   isLoading: false,
  //   isPending: false,
  //   isPlaceholderData: false,
  //   isRefetching: false,
  //   isSuccess: false,
  //   wsConnected: false,
  //   fetchNextPage: noop,
  //   fetchPreviousPage: noop,
  //   refetch: noop,
  //   renderPayload: noop,
  //   reset: noop,
  // };
  const isFetching = bank?.isFetching || fetchingStates?.baseFetching;
  const isSuccess =
    (!!bank && 'data' in bank && !!bank?.data?.data && !bank.error) ||
    (bank?.data?.data?.length === 0 && !bank.isPending && !isFetching);
  const isPending = !isSuccess || fetchingStates?.pending;
  const isEmpty = isSuccess && bank?.data?.data?.length === 0 && !bank?.isPending;

  const payload = useMemo(
    () => ({
      ...bank,
      isPending,
      isEmpty,
      isSuccess,
      isFetching,
      isFetchingNextPage: fetchingStates.nextFetching,
      fetchNextPage,
      isFetchingPreviousPage: fetchingStates.prevFetching,
      fetchPreviousPage,
      newestItem,
      oldestItem,
      hasNextPage,
      hasPreviousPage,
      refetch,
      renderPayload,
      reset,
      wsConnected: ws.ready,
    }),
    [
      bank,
      isPending,
      isEmpty,
      isSuccess,
      isFetching,
      fetchingStates,
      fetchNextPage,
      fetchPreviousPage,
      newestItem,
      oldestItem,
      hasNextPage,
      hasPreviousPage,
      refetch,
      renderPayload,
      reset,
      ws.ready,
    ],
  );

  // Add a tiny delay in case we get a bunch of updates at once
  // Also useful for preventing flash of loading icon for <100ms
  const [debouncedPayload] = useDebounce(payload, 100);

  // Then, on the client, refetch the data from scratch if it hasn't been loaded already
  useEffectOnce(() => {
    if (
      !deepMemoOpts?.pause &&
      !basePayload.isLoading &&
      (basePayload?.isPending || (isSuccess && storeBank?.data?.data))
    ) {
      // Note that storeBank.data may already contain cached messages. So the initial load of this page will render them
      refetch();
    }
  });

  const refetchRef = useRefUpdating(refetch);

  const _basePayloadRef = useRefUpdating(basePayload);
  const _storeBankRef = useRefUpdating(storeBank);
  const isSuccessRef = useRefUpdating(isSuccess);
  const isPendingRef = useRefUpdating(basePayload?.isPending);
  const isLoadingRef = useRefUpdating(basePayload?.isLoading);
  const hasDataRef = useRefUpdating(!!storeBank?.data?.data);
  const isErrorRef = useRefUpdating(storeBank?.isError || !!storeBank?.error);

  // Lastly, on the client, refetch the data from scratch if the base params were changed
  useUpdateEffect(
    useDebouncedCallback(() => {
      if (
        !initialOpts.manual &&
        !isLoadingRef.current &&
        (isPendingRef.current ||
          (isSuccessRef.current && hasDataRef) ||
          (!isSuccessRef.current && !isErrorRef.current))
      ) {
        // Note that storeBank.data may already contain cached messages. So the initial load of this page will render them
        refetchRef.current?.();
      }
    }, 250),
    [initialOpts],
  );

  return debouncedPayload;
}
